//@ts-nocheck
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Form, Input, Button, Row, Col, Divider, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useIntl, FormattedMessage } from 'react-intl';
import { toPairs, pick } from 'ramda';

import { commonFormCssMixin } from 'Common/Components/Form';
import InfoBox from 'Common/Components/InfoBox';
import withLoading from 'Common/HOC/loadingOr';
import type { SettingsData } from 'utils/apiClient/services/rksv/types';

const Title = styled.h3`
  margin: 0;
  line-height: 32px;
`;

interface Props {
  disableFields: boolean;
  data: SettingsData | undefined;
  handleSubmit: (data: FormValues) => void;
  handleReset: () => void;
}

export type FormValues = Pick<
  SettingsData,
  'cashboxId' | 'token' | 'terminalId'
>;

const SettingsForm: React.FC<Props> = ({
  handleSubmit,
  handleReset,
  data,
  disableFields,
}) => {
  const [form] = Form.useForm<FormValues>();
  const intl = useIntl();
  const [isFormTouched, setIsFormTouched] = useState(false);

  useEffect(() => {
    form.resetFields();
    if (data) {
      form.setFields([
        ...toPairs(pick(['cashboxId', 'token', 'terminalId'], data)).map(
          (f) => ({
            name: f[0],
            value: f[1],
          })
        ),
      ]);
    }

    return () => setIsFormTouched(false);
  }, [form, data]);

  const resetHandler = () => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({
        defaultMessage:
          'Are you sure you want to reset your cashbox data? Doing so will stop signing receipts',
      }),
      okText: intl.formatMessage({ defaultMessage: 'Confirm' }),
      cancelText: intl.formatMessage({ defaultMessage: 'Cancel' }),
      onOk: () => {
        form.resetFields();
        handleReset();
        setIsFormTouched(false);
      },
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => {
        handleSubmit(values);
        setIsFormTouched(false);
      }}
      hideRequiredMark
      onValuesChange={() => {
        if (!isFormTouched) {
          setIsFormTouched(true);
        }
      }}
      css={`
        ${() => commonFormCssMixin};
      `}
    >
      <Row justify="space-between" css="padding: 1em">
        <Title>
          <FormattedMessage defaultMessage="Cashbox Data" />
        </Title>
        <Col css="display: flex; justify-content: flex-end;">
          <Button type="primary" htmlType="submit" disabled={!isFormTouched}>
            <FormattedMessage defaultMessage="Save" />
          </Button>
          <Button type="default" onClick={resetHandler} css="margin-left: 8px;">
            <FormattedMessage defaultMessage="Reset" />
          </Button>
        </Col>
      </Row>
      <Divider css="margin: 0" />
      <Row css="padding: 1em">
        <Col span={12}>
          <Form.Item
            name="cashboxId"
            label={intl.formatMessage({ defaultMessage: 'Cashbox ID' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  defaultMessage: 'This field is required',
                }),
              },
            ]}
          >
            <Input
              style={{ width: 400, float: 'left' }}
              disabled={disableFields}
            />
          </Form.Item>
          <Form.Item
            name="token"
            label={intl.formatMessage({
              defaultMessage: 'Access Token',
              description: 'rksv setup',
            })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  defaultMessage: 'This field is required',
                }),
              },
            ]}
          >
            <Input disabled={disableFields} />
          </Form.Item>
          <Form.Item
            name="terminalId"
            label={intl.formatMessage({ defaultMessage: 'Terminal ID' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  defaultMessage: 'A Terminal ID is required',
                  description: 'rksv setup',
                }),
              },
            ]}
          >
            <Input
              style={{ width: 150, float: 'left' }}
              disabled={disableFields}
            />
          </Form.Item>
        </Col>
        <Col span={12} css="padding: 8px">
          <InfoBox
            text={
              <p>
                <FormattedMessage
                  defaultMessage="On this page you can connect your signature unit from <link>Fiskaltrust</link>. Please follow these steps:"
                  values={{
                    link: (txt: React.ReactNode) => (
                      <a
                        href="https://www.fiskaltrust.at"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {txt}
                      </a>
                    ),
                  }}
                />
                <br />
                1.
                <FormattedMessage defaultMessage="Please enter the information of your Fiskaltrust signature creation unit here" />
                <br />
                2.
                <FormattedMessage defaultMessage="Please save and automatically create the initial receipt" />
                <br />
                3.
                <FormattedMessage defaultMessage="Scan the QR Code with the official BMF Belegcheck App" />
                <br />
              </p>
            }
          />
        </Col>
      </Row>
    </Form>
  );
};

export default withLoading(SettingsForm);
